// Asset store for centralizing fetching assets based on URL
import type {
	SbMediaStoryblok,
	AssetStoryblok,
	ListItemVideoSlideStoryblok,
} from '@/sbTypes'
import type { Cacheable } from '@/types'
import { captureException } from '@sentry/vue'
import localforageLib from 'localforage'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

/** IndexedDB for storing assets offline */
const localAssets = localforageLib.createInstance({
	name: 'content',
	storeName: 'assets',
})

/** IndexedDB for keeping track of asset store version */
const localVersions = localforageLib.createInstance({
	name: 'content',
	storeName: 'versions',
})

export type Asset = {
	arrayBuffer: ArrayBuffer
	type: string
}

export type StoredAsset = Cacheable<Asset>

export type GetAssetOptions = {
	verify?: boolean
	prefetch?: boolean

	/** If not found locally, fail */
	noNetwork?: boolean
}

/** Type representing an object from SB that might have a videoUrl */
type VideoHaver = ListItemVideoSlideStoryblok | SbMediaStoryblok

export const useAssetStore = defineStore('asset', () => {
	let initialized = false

	/** List of asset URLs that are in the cache */
	const cachedAssetUrls = ref<string[]>([])

	/** Map of asset URLs to blob URLs */
	const assetBlobUrls: Map<string, string> = new Map()

	/** Map of asset URLs to promises which are resolved with Blob URLs */
	const assetBlobUrlPromises: Map<
		string,
		Promise<string | undefined>
	> = new Map()

	/** Map of asset URLs to promises which are resolved when the asset is cached */
	const assetPromises: Map<string, Promise<Blob>> = new Map()

	const startedAssetPromiseCount = ref(0)
	const completedAssetPromiseCount = ref(0)

	const assetDownloadPercentage = computed(() => {
		return startedAssetPromiseCount.value === 0
			? undefined
			: completedAssetPromiseCount.value / startedAssetPromiseCount.value
	})

	/** Initializes the asset store. Optional to call. Useful for tests. */
	async function initialize() {
		if (initialized) {
			return
		}

		await processVersionUpgrades()
		cachedAssetUrls.value = await localAssets.keys()
		initialized = true
	}

	/** Get the storage schema version of the asset store */
	async function getVersion(): Promise<number> {
		const version = await localVersions.getItem<string>('assets')
		return version ? parseInt(version) : 0
	}

	/** Set the storage schema version of the asset store */
	async function setVersion(version: number) {
		await localVersions.setItem('assets', version.toString())
	}

	/**
	 * Version 1: Data needs to be stored as ArrayBuffer
	 * Just delete the data and re-download. This has the least
	 * chance of failing.
	 */
	async function upgradeToV1() {
		await localAssets.clear()
	}

	/** Make sure the asset store is in the schema expected by the code */
	async function processVersionUpgrades() {
		const version = await getVersion()

		// Version 0: Store data as blob

		if (version < 1) {
			await upgradeToV1()
			await setVersion(1)
		}
	}

	/** Saves a downloaded asset to the cache */
	async function cacheAsset(url: string, asset: StoredAsset) {
		asset.cachedDate = new Date()
		cachedAssetUrls.value.push(url)
		await localAssets.setItem(url, asset)
	}

	/** Fetch and cache an asset */
	async function fetchAndCacheAsset(url: string, prevEtag?: string) {
		let init: RequestInit = {}
		if (prevEtag)
			init.headers = {
				'If-None-Match': prevEtag,
			}
		const response = await fetch(url, init)
		let etag = response.headers.get('etag')

		if (response.status === 304) {
			// We shouldn't ever get undefined if we already have the etag
			// TODO: don't assume
			let asset = (await getAssetFromCache(url)) as StoredAsset
			return new Blob([asset.data.arrayBuffer], { type: asset.data.type })
		}

		const blob = await response.blob()

		try {
			cacheAsset(url, {
				data: {
					arrayBuffer: await blob.arrayBuffer(),
					type: blob.type,
				},
				etag: etag || '',
			})
		} catch (error) {
			console.error(error)
			captureException(error)
		}
		return blob
	}

	/** Returns a Promise that resolves with blob URL for an asset */
	async function getAssetBlobUrl(
		url: string,
		options: GetAssetOptions = {}
	): Promise<string | undefined> {
		initialized || (await initialize())

		// If we have already computed a blob URL for this asset, return it
		if (assetBlobUrls.has(url)) {
			return assetBlobUrls.get(url)
		}

		// If we already have a blob url promise for this URL, return it
		if (assetBlobUrlPromises.has(url)) {
			return assetBlobUrlPromises.get(url)
		}

		// Create a promise for getting the blob url
		const blobUrlPromise = getAsset(url, options).then((blob) => {
			if (!blob) {
				throw new Error(`Asset not found: ${url}`)
			}
			// Ensure PDF content type is set correctly
			const contentType = url.toLowerCase().endsWith('.pdf')
				? 'application/pdf'
				: blob.type
			const blobWithCorrectType = new Blob([blob], { type: contentType })
			const blobUrl = URL.createObjectURL(blobWithCorrectType)
			assetBlobUrls.set(url, blobUrl)
			return blobUrl
		})

		assetBlobUrlPromises.set(url, blobUrlPromise)

		return blobUrlPromise
	}

	/** Returns a Promise that resolves when the asset is cached */
	async function getAsset(
		url: string,
		{ verify }: GetAssetOptions = {}
	): Promise<Blob | undefined> {
		await initialize()

		const fetchAndCacheThenReturnPromise = (url: string, etag?: string) => {
			const fetchPromise = fetchAndCacheAsset(url, etag)

			startedAssetPromiseCount.value++
			fetchPromise.then(() => completedAssetPromiseCount.value++)
			assetPromises.set(url, fetchPromise)
			return fetchPromise
		}

		// If we already have an asset promise for this URL, return it
		if (assetPromises.has(url)) {
			return assetPromises.get(url)
		}

		// If we have already cached this asset, return it
		if (cachedAssetUrls.value.includes(url)) {
			startedAssetPromiseCount.value++
			const storedAssetPromise = getAssetFromCache(url, verify).then(
				(asset) => {
					completedAssetPromiseCount.value++
					if (!asset) {
						throw new Error(`Asset not found: ${url}`)
					}
					return new Blob([asset.data.arrayBuffer], { type: asset.data.type })
				}
			)
			assetPromises.set(url, storedAssetPromise)
			return storedAssetPromise
		}

		return fetchAndCacheThenReturnPromise(url)
	}

	/** Gets an asset from the cache */
	async function getAssetFromCache(
		url: string,
		verify = false
	): Promise<StoredAsset | undefined> {
		let asset = await localAssets.getItem<StoredAsset>(url)

		if (!asset) {
			// Cache miss
			return undefined
		}

		// If we otherwise wouldn't verify, do it anyway if the asset is older than a week
		const oneWeekAgo = new Date().getTime() - 1000 * 60 * 60 * 24 * 7
		if (
			!verify &&
			asset?.cachedDate &&
			asset.cachedDate.getTime() < oneWeekAgo
		) {
			verify = true
		}

		// If the client wants to verify the cache, refetch using ETag
		if (verify) {
			// If we just got the asset, ignore
			if (
				asset.cachedDate &&
				asset.cachedDate.getTime() > Date.now() - 60 * 1000
			) {
				return asset
			}

			let response: Response | null = null

			// prevent errors from breaking images: verifying is optional
			try {
				response = await fetch(url, {
					headers: {
						'If-None-Match': asset.etag,
					},
				})
			} catch (error) {
				// do nothing
			}

			// 200 means updated asset
			if (response?.status === 200) {
				const blob = await response.blob()
				asset = {
					data: {
						arrayBuffer: await blob.arrayBuffer(),
						type: blob.type,
					},
					etag: response.headers.get('etag') || '',
				}
				await cacheAsset(url, asset)
			}

			// If not 200, we either got 304 which means no update, or an error; fall back to cached asset
		}

		return asset
	}

	/** Returns true if the asset is cached */
	function isAssetCached(url: string) {
		return cachedAssetUrls.value.includes(url)
	}

	/**
	 * Remove all cached items that are not in the given list
	 */
	function deleteUnusedAssets(keys: string[]) {
		return new Promise<void>((resolve) => {
			// Remove items from memory lists
			cachedAssetUrls.value = cachedAssetUrls.value.filter((url) =>
				keys.includes(url)
			)

			for (const url of assetBlobUrlPromises.keys()) {
				if (!keys.includes(url)) {
					assetBlobUrlPromises.delete(url)
				}
			}

			for (const url of assetPromises.keys()) {
				if (!keys.includes(url)) {
					assetPromises.delete(url)
				}
			}

			// Remove items from local storage
			localAssets
				.iterate((_value, key) => {
					if (!keys.includes(key)) {
						localAssets.removeItem(key)
					}
				})
				.then(() => {
					resolve()
				})
		})
	}

	function getDocumentUrl(url: string) {
		return url.replace(
			'https://precoa-web-content.s3.us-west-2.amazonaws.com/Sales+Studio',
			import.meta.env.VITE_SALES_STUDIO_API_URL + '/api/proxy/aws'
		)
	}

	/**
	 * @param asset Storyblok asset
	 * @returns URL to get the asset form the image service
	 */
	function getStoryblokImageUrl(asset: AssetStoryblok) {
		if (!asset.filename) {
			return undefined
		}

		// Use webp format for best compression unless original is SVG
		const format =
			asset.filename.includes('storyblok.com') &&
			!asset.filename.endsWith('.svg')
				? '/m/filters:format(webp)'
				: ''

		return (
			// Use alt server that supports CORS
			asset.filename.replace(
				'https://a-us.storyblok.com',
				`${import.meta.env.VITE_SALES_STUDIO_API_URL}/api/proxy/storyblok-image`
			) + format
		)
	}

	function getThePlanningCenterImageUrl(url: string) {
		if (!url) {
			return ''
		}
		return url.replace(
			'https://image.email.theplanningcenter.org',
			`${import.meta.env.VITE_SALES_STUDIO_API_URL}/api/proxy/theplanningcenter`
		)
	}

	/** Get the URL to get the asset from the video service */
	function getVideoUrl(video: VideoHaver) {
		if (!video.videoUrl || typeof video.videoUrl !== 'string') {
			return undefined
		}

		return (
			// Use alt server that supports CORS
			video.videoUrl.replace(
				'https://a-us.storyblok.com',
				`${import.meta.env.VITE_SALES_STUDIO_API_URL}/api/proxy/storyblok-image`
			)
		)
	}

	/** Given an Asset, returns a promise that resolves with a blob URL */
	function getImageBlobUrl(asset: AssetStoryblok) {
		const url = getStoryblokImageUrl(asset)
		return url ? getAssetBlobUrl(url) : undefined
	}

	/** Given a SbMedia, returns a promise that resolves with a blob URL */
	function getVideoBlobUrl(video: VideoHaver) {
		const formattedUrl = getVideoUrl(video)
		return formattedUrl ? getAssetBlobUrl(formattedUrl) : undefined
	}

	/** Remove all cached getAsset attempts so that assets can be reverified */
	function clearAssetPromises() {
		assetBlobUrlPromises.clear()
		assetPromises.clear()
	}

	/** Delete everything */
	function purge() {
		initialized = false
		cachedAssetUrls.value = []
		assetBlobUrls.clear()
		assetBlobUrlPromises.clear()
		assetPromises.clear()
		startedAssetPromiseCount.value = 0
		completedAssetPromiseCount.value = 0
		return localAssets.clear()
	}

	return {
		assetDownloadPercentage,
		clearAssetPromises,
		deleteUnusedAssets,
		getAsset,
		getAssetBlobUrl,
		getDocumentUrl,
		getImageBlobUrl,
		getStoryblokImageUrl,
		getThePlanningCenterImageUrl,
		getVideoBlobUrl,
		getVideoUrl,
		isAssetCached,
		initialize,
		purge,
	}
})
